import { useCheckGeneralInsuranceQuotesExistQuery } from '@acre/graphql'

const GI_MANUAL_PROVIDER: (GeneralInsuranceProvider | 'MANUAL')[] = [
  'MANUAL',
  'BENNETT_ALEXANDER',
  'TOWERGATE',
  'GALLAGHER',
  'THE_SOURCE',
  'ASSURANT',
]

type UseSourcedGIProductsProps = {
  caseId: string
  giProducts: Maybe<CreateGeneralInsuranceProduct[]>
}

// Given a list of GI products, return all sourced GI products
export const useSourcedGIProducts = ({ caseId, giProducts }: UseSourcedGIProductsProps) => {
  const { lvProducts, otherProducts } = (giProducts || []).reduce(
    (acc, giProduct) => {
      const provider = giProduct.details?.provider as GeneralInsuranceProvider

      if (provider === 'LV') {
        acc.lvProducts.push(giProduct)
      } else {
        acc.otherProducts.push(giProduct)
      }
      return acc
    },
    { lvProducts: [], otherProducts: [] } as {
      lvProducts: CreateGeneralInsuranceProduct[]
      otherProducts: CreateGeneralInsuranceProduct[]
    },
  )

  const lvQuoteReferences = lvProducts
    ?.map((giProduct) => giProduct.details?.quote_reference)
    ?.filter((quote_reference): quote_reference is string => Boolean(quote_reference))

  const { data: giQuotesExistData, loading: checkingGIQuotesExist } = useCheckGeneralInsuranceQuotesExistQuery({
    variables: { case_id: caseId, quote_ids: lvQuoteReferences || [] },
    skip: !caseId || !lvQuoteReferences.length,
  })
  const existingGIQuotes = new Set(giQuotesExistData?.checkGeneralInsuranceQuotesExist || [])
  const sourcedLVProducts = lvProducts.filter(
    (product) => product.details?.quote_reference && existingGIQuotes.has(product.details?.quote_reference),
  )

  const otherSourcedProducts = otherProducts.filter(
    (product) =>
      product.details?.provider && !GI_MANUAL_PROVIDER.includes(product.details.provider as GeneralInsuranceProvider),
  )

  const sourcedGIProducts = sourcedLVProducts.concat(otherSourcedProducts)

  return {
    loading: checkingGIQuotesExist,
    sourcedGIProducts: sourcedGIProducts,
  }
}
